// React
import { useState, useEffect, useRef } from "react";

// Third-party
import { useQueryClient } from "@tanstack/react-query";

// Recoil
import { useSetRecoilState } from "recoil";

// Components
import Settings from "../compoenets/Settings";
import { SpaceSideBar } from "../compoenets/ProjectFilter/SideBarMenu/SpaceSideBar";

import { TaskDetailWrapper } from "./TaskDetailWrapper";
import Toast from "./Toast";
import GuestPopup from "./Popup/GuestPopup/GuestPopup";
import RecurringPopup from "./Popup/RecurringPopup/RecurringPopup";
import ChangeLogPopup from "./Popup/ChangelogPopup/ChangeLogPopup";

// Hooks / Queries
import { useGoogleContactsQueryOptions } from "../react-query/contact/queries";
import { useBlockSync } from "../react-query/useBlockSync";
import { useFetchUserInfoQuery } from "../react-query/user/queries";

// Styles
import styles from "./Main.module.css";
import { AppPathComposer } from "../api/genPath";
import { spaceQueryKeys } from "../react-query/space/queries";
import { juneIdentify } from "../utils/june/analytics";
import { useNavigate } from "react-router-dom";
import DesktopMainView from "../compoenets/mainComponents/DesktopMainView";
import MobileMainView from "../compoenets/mainComponents/MobileMainView";
import { useIsMobileDevice } from "../hooks/mobile/useIsMobileDevice";
import { USER_ROLE } from "../types/account/enum";
import { usePreferenceSync } from "../hooks/preference/usePreferenceSync";
import useVersionCheck from "../hooks/user/useVersionCheck";
import { useRepairRecurringLinks } from "../hooks/block/useRepairRecurringLinks";

export default function Main() {
  const [showSettingsPopup, setShowSettingsPopup] = useState<boolean>(false);
  const navigate = useNavigate();

  const { isNewVersionAvailable, reloadPage } = useVersionCheck();

  // NOTE 사용자 정보를 불러오고, Recoil에 저장하고, 프로젝트 목록도 초기화
  const userInfoQuery = useFetchUserInfoQuery();
  const syncPreference = usePreferenceSync();

  const guestPopupRef = useRef(null);
  const recurringPopupRef = useRef(null);
  const isMobileDevice = useIsMobileDevice();

  /** react-query */
  const queryClient = useQueryClient();
  const googleContactOptions = useGoogleContactsQueryOptions(); // 최상위 레벨에서 훅 호출
  /** Server로부터 block들이 Google Calendar와 동기화되었는지 여부 */
  const { isFetching } = useBlockSync();
  /** 일단 variable 이름은 직관적으로 당겨올 준비가 됐다는 의미에서 hasBlocksSynchronizedWithGoogle로 정의 */
  const hasBlocksSynchronizedWithGoogle = !isFetching;

  // TEMP: 임시 반복 수정 로직
  useRepairRecurringLinks(hasBlocksSynchronizedWithGoogle);

  // 새로운 버전이 있으면 페이지 새로고침
  useEffect(() => {
    if (isNewVersionAvailable) {
      reloadPage();
    }
  }, [isNewVersionAvailable]);

  useEffect(() => {
    if (userInfoQuery.isSuccess && userInfoQuery.data) {
      const user = userInfoQuery.data;

      if (user.role === USER_ROLE.TRIAL) {
        navigate(AppPathComposer.joinWaitList);
      } else {
        juneIdentify(user.email, user.role);
        syncPreference(user.preference);
        queryClient.invalidateQueries({ queryKey: spaceQueryKeys.all });
      }
    }
  }, [userInfoQuery.isSuccess, userInfoQuery.data]);

  useEffect(() => {
    if (userInfoQuery.isError) {
      console.error("Failed to load user info:", userInfoQuery.error);
    }
  }, [userInfoQuery.isError, userInfoQuery.error]);

  useEffect(() => {
    queryClient.prefetchQuery(googleContactOptions);
  }, []);

  const toggleSettingsPopup = () => {
    setShowSettingsPopup((prev: boolean) => !prev);
  };

  return (
    <div className={styles["main"]}>
      <SpaceSideBar onClickSettings={() => toggleSettingsPopup()} />
      {isMobileDevice ? (
        <MobileMainView hasBlocksSynchronizedWithGoogle={hasBlocksSynchronizedWithGoogle} />
      ) : (
        <DesktopMainView hasBlocksSynchronizedWithGoogle={hasBlocksSynchronizedWithGoogle} />
      )}

      {showSettingsPopup && (
        <Settings
          settingState={showSettingsPopup}
          onClose={() => toggleSettingsPopup()}
        />
      )}

      <TaskDetailWrapper
        guestPopupRef={guestPopupRef}
        recurringPopupRef={recurringPopupRef}
      />

      <Toast />
      <GuestPopup guestPopupRef={guestPopupRef} />
      <RecurringPopup recurringPopupRef={recurringPopupRef} />
      <ChangeLogPopup />
    </div>
  );
}
